/**
 * LLM Router — switches between Cloud (Grafton backend)
 * and Local (user's Ollama on localhost:11434 → Gemma2:9b or any local model).
 *
 * Local mode:
 *   - The request goes from the user's browser DIRECTLY to their Ollama.
 *   - Zero data touches our backend / cloud.
 *   - Requires Ollama to allow the frontend origin:
 *       OLLAMA_ORIGINS=* ollama serve     (easiest, dev)
 *     or
 *       OLLAMA_ORIGINS=https://dashboard.grafton-ia.com ollama serve
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Les petits modèles locaux se perdent dans un contexte de 12 000 caractères : on ne leur donne que l'essentiel.
function compactForLocal(ctx: Record<string, unknown>): Record<string, unknown> {
  const keys = ["CHIFFRES_CLES_A_CITER_TELS_QUELS", "cadrage_strategique"];
  const out: Record<string, unknown> = {};
  for (const k of keys) if (k in ctx) out[k] = ctx[k];
  if (!("CHIFFRES_CLES_A_CITER_TELS_QUELS" in out)) return ctx;
  out.financements_ouverts = "CIR 150 k€ (échéance 31/12/2026), Horizon Europe 92 k€ (échéance 01/11/2026) : jamais du CA.";
  return out;
}

export type LLMMode = "cloud" | "local";

export interface LLMAnalyzeArgs {
  context: Record<string, unknown>;
  userQuestion?: string;
  localModel?: string;     // default gemma2:9b
  localUrl?: string;       // default http://localhost:11434
}

export interface LLMAnalyzeResult {
  ok: boolean;
  provider: string;
  analysis: string;
  error?: string;
}

/** Cloud analysis via backend (Grafton IA). */
async function analyzeCloud(args: LLMAnalyzeArgs): Promise<LLMAnalyzeResult> {
  const r = await fetch(`${BACKEND_URL}/api/cfo/analyze`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      context: args.context,
      user_question: args.userQuestion,
    }),
  });
  const d = await r.json();
  return {
    ok: !!d.ok,
    provider: d.provider || "deepseek-chat",
    analysis: d.analysis || "",
    error: d.error,
  };
}

/** Local analysis — browser → Ollama on localhost. Data never leaves the user's machine. */
async function analyzeLocal(args: LLMAnalyzeArgs): Promise<LLMAnalyzeResult> {
  const base = args.localUrl || "http://localhost:11434";
  const model = args.localModel || "gemma2:9b";

  // Fetch the same system prompt used in cloud mode for consistency
  let systemPrompt = "";
  try {
    const sysRes = await fetch(`${BACKEND_URL}/api/cfo/system-prompt`, { credentials: "include" });
    if (sysRes.ok) systemPrompt = (await sysRes.json()).system || "";
  } catch { /* tolerate */ }

  const question = args.userQuestion || "Analyse les revenus et propose un plan d'action.";
  const userMsg =
    `Contexte financier JSON :\n${JSON.stringify(compactForLocal(args.context), null, 2)}\n\n` +
    `Question du dirigeant : ${question}`;

  try {
    const r = await fetch(`${base}/api/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model,
        messages: [
          ...(systemPrompt ? [{ role: "system", content: systemPrompt }] : []),
          { role: "user", content: userMsg },
        ],
        stream: false,
      }),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const d = await r.json();
    const analysis = d?.message?.content || "";
    return { ok: !!analysis, provider: `ollama:${model}`, analysis, error: analysis ? undefined : "Réponse vide" };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    const friendly = msg.includes("Failed to fetch") || msg.includes("NetworkError")
      ? "Impossible d'atteindre Ollama sur localhost:11434. Vérifie qu'Ollama tourne et autorise cette origine."
      : msg;
    return { ok: false, provider: `ollama:${model}`, analysis: "", error: friendly };
  }
}

/** Main entry point — routes to cloud or local based on mode. */
export async function llmAnalyze(mode: LLMMode, args: LLMAnalyzeArgs): Promise<LLMAnalyzeResult> {
  return mode === "local" ? analyzeLocal(args) : analyzeCloud(args);
}

/** Quick reachability probe for the Local mode (to show a green/red dot in the UI). */
export async function probeLocalOllama(localUrl = "http://localhost:11434"): Promise<{ reachable: boolean; models?: string[]; error?: string }> {
  try {
    const r = await fetch(`${localUrl}/api/tags`, { method: "GET" });
    if (!r.ok) return { reachable: false, error: `HTTP ${r.status}` };
    const d = await r.json();
    const models = Array.isArray(d?.models) ? d.models.map((m: { name: string }) => m.name) : [];
    return { reachable: true, models };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    return { reachable: false, error: msg };
  }
}
